"use client";

import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";

export type SelectDropdownOption = {
  value: string;
  label: string;
};

type SelectDropdownProps = {
  label?: string;
  value: string;
  options: SelectDropdownOption[];
  onChange: (value: string) => void;
  ariaLabel?: string;
  id?: string;
  className?: string;
};

/** Labeled full-width select built on the shared DropdownMenu. */
export function SelectDropdown({
  label,
  value,
  options,
  onChange,
  ariaLabel,
  id,
  className,
}: SelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const selected =
    options.find((option) => option.value === value) ?? options[0];
  const labelId = id && label ? `${id}-label` : undefined;

  return (
    <div className={className}>
      {label ? (
        <p
          id={labelId}
          className="mb-1.5 block text-xs font-medium text-aera-gray"
        >
          {label}
        </p>
      ) : null}
      <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            id={id}
            aria-label={ariaLabel ?? label}
            aria-labelledby={labelId}
            aria-expanded={open}
            className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-aera-lightgray bg-aera-white py-2.5 pl-3 pr-3 text-left text-sm font-semibold text-aera-charcoal outline-none"
          >
            <span className="min-w-0 truncate">{selected?.label ?? ""}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-aera-gray transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
              aria-hidden
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          collisionPadding={16}
          className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <DropdownMenuItem
                key={option.value}
                className={
                  isSelected
                    ? "cursor-pointer font-semibold text-aera-charcoal"
                    : "cursor-pointer"
                }
                onSelect={() => onChange(option.value)}
              >
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {isSelected ? (
                  <Check
                    className="ml-2 h-4 w-4 shrink-0 text-aera-charcoal"
                    strokeWidth={2.5}
                    aria-hidden
                  />
                ) : (
                  <span className="ml-2 h-4 w-4 shrink-0" aria-hidden />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
